import React from 'react';
import classnames from 'classnames';
import {Link, withRouter} from 'react-router';
import PropTypes from 'prop-types';
import {isEmpty} from 'lodash';
import qs from 'qs';
import {withApplicationMetadata} from 'client/Routing/withApplicationMetadata';
import sessionStore from 'client/Session/SessionStore';
import {pluralize} from 'lib/stringUtils';
import constants from 'client/constants';
import {showGlobalLoginModal} from 'client/LogIn/utils';
import appStore from 'client/AppStore';
import ImmutablePropTypes from 'react-immutable-proptypes';
import {getQueryParamsAsObject, removeQueryParams} from 'client/history';

import {
  canCreateTemplateForSubject,
  getUserSubjectAccessQuery,
  isUnlicensedTeacherOrAdmin,
  userLicensedSubjectsQuery
} from 'lib/UserAccessUtil';
import LoadingIndicator from 'generic/LoadingIndicator.react';
import {Heading, LoadingSpinner, Icon, WindowSizeConsumer, WithTooltip} from '@albert-io/atomic';
import UpgradeSubjectModal from 'components/PracticeView/UpgradeSubjectModal/UpgradeSubjectModal.react';
import ActionsDropdown from 'client/generic/StudyGuideItem/Teacher/ActionsDropdown/ActionsDropdown.react';
import {
  RestrictionIndicator,
  buildGuideTree
} from 'client/components/ContentDiscovery/ManageRestrictionsModal';
import {
  TeacherRestrictionIndicator,
  getTeacherRestrictedGuideLevelIds,
  getGuideLevelIdsWithLockedDescendant
} from 'client/components/TeacherRestrictedContent';
import AccuracyBadge from 'client/generic/AccuracyBadge/AccuracyBadge';
import MarkdownRendererV2 from 'generic/MarkdownRendererV2/MarkdownRendererV2.react';
import {
  StudyGuideItemList,
  StudyGuideItem
} from 'client/generic/StudyGuideItem/StudyGuideItem.react';
import AdvancedSearch from 'client/components/AdvancedSearch/AdvancedSearch.react';
import {getGuideLevelsBySearchParams} from 'client/components/AdvancedSearch/AdvancedSearch.queries';
import useMandarkQueries from 'lib/hooks/useMandarkQueries';
import {GuideLevelsCollectionModel} from 'resources/GuideLevel/GuideLevelsCollection.model';
import {GuideModelV1} from 'resources/augmented/Guide/GuideModel.v1';
import {UserModelV2} from 'resources/GeneratedModels/User/UserModel.v2';

import {showPublicViewForSubject} from 'components/PracticeView/practiceView.utils';

import {List} from 'immutable';

import {getSearchParams, isSearchMode} from '../components/AdvancedSearch/AdvancedSearch.utils';
import {hasStandardsQuery, hasTagsQuery} from '../components/AdvancedSearch/AdvancedSearch.queries';

import MarketingBanner from './MarketingBanner/MarketingBanner.react';
import studyGuideUtils from './utils';
import {ThemeMenu} from './ThemeMenu/ThemeMenu';

import './study-guide.scss';

const NoResultsFound = () => (
  <div className='mt-x2 rounded-x1 border-border-primary background-primary p-x5 w-full border border-solid'>
    <Heading as='center' size='m'>
      No matches found
    </Heading>
  </div>
);

export class GuideLevelsList extends React.Component {
  static propTypes = {
    guide: PropTypes.instanceOf(GuideModelV1),
    guideLevelsBySearchParams: PropTypes.instanceOf(GuideLevelsCollectionModel),
    location: PropTypes.shape({
      hash: PropTypes.string,
      query: PropTypes.shape({
        s: PropTypes.string,
        standards: PropTypes.string,
        tags: PropTypes.string
      }),
      search: PropTypes.string
    }),
    subject: PropTypes.object,
    user: PropTypes.instanceOf(UserModelV2),
    licensedSubjects: ImmutablePropTypes.list,
    usePublicView: PropTypes.bool,
    showAdvancedSearch: PropTypes.bool,
    onSearchCallback: PropTypes.func,
    isReady: PropTypes.bool,
    partialCreditEnabled: PropTypes.bool
  };

  constructor() {
    super();
    this.state = {
      shouldShowUpgradeSubjectModal: false,
      hasPopover: false
    };
    this.autoOpenedForSubjectId = null;
  }

  componentDidMount() {
    window.addEventListener('scroll', this.handleScroll);
    this.bootstrapComponentState();
  }

  componentDidUpdate(prevProps) {
    // We may only need to compare if the guide changes but we are comparing all props to be safe
    if (
      prevProps.location?.query?.s !== this.props.location?.query?.s ||
      prevProps.guide !== this.props.guide ||
      prevProps.subject !== this.props.subject ||
      prevProps.licensedSubjects !== this.props.licensedSubjects
    ) {
      this.bootstrapComponentState();
    }
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.handleScroll);
  }

  bootstrapComponentState() {
    const {guide, subject, licensedSubjects} = this.props;
    if (
      sessionStore.hasValidSession() &&
      !licensedSubjects.isEmpty() &&
      !subject.doesUserHaveAccess() &&
      this.autoOpenedForSubjectId !== subject.getId()
    ) {
      this.autoOpenedForSubjectId = subject.getId();
      this.openUpgradeSubjectModal();
    }

    const guideLevels = guide
      .getSortedGuideLevels()
      .filter((guideLevel) => guideLevel.hasQuestions())
      .filter(this.shouldDisplayGuideLevel);
    const themes = guideLevels.filter((guideLevel) => guideLevel.getNlevel() === 1);
    const themeRefs = themes.reduce((acc, theme) => {
      const ref = React.createRef();
      return {
        ...acc,
        [theme.getId()]: ref
      };
    }, {});

    // add scroll to slug behavior (see https://github.com/albert-io/project-management/issues/4140)
    const themeFromUrl = themes.find(
      (theme) => theme.getUrlSlug() === this.props.location.hash.substring(1)
    );

    this.setState({
      subject,
      licensedSubjects,
      themes,
      themeRefs,
      initialTheme: themeFromUrl?.getId()
    });
  }

  getMatchingQuestionCount = (guideLevel) => {
    const matchingSearchResult = this.props.guideLevelsBySearchParams
      .getCollection()
      .find((result) => result.getId() === guideLevel.getId());

    return matchingSearchResult.getMeta().getTotalMatchingQuestions() || 0;
  };

  shouldDisplayGuideLevel = (guideLevel) => {
    if (!isSearchMode()) {
      return true;
    }
    const matchingGuideLevelIds = this.props.guideLevelsBySearchParams.cache('f', () => {
      return this.props.guideLevelsBySearchParams
        .getCollection()
        .toSet()
        .map((level) => level.getId());
    });
    return matchingGuideLevelIds.includes(guideLevel.getId());
  };

  generateActionsDropdown(
    guideLevel,
    hasAccessToCreateTemplate,
    linkToQuestions,
    isAssessment,
    guideLevelTree
  ) {
    if (isSearchMode() || (!guideLevel.isFree() && !hasAccessToCreateTemplate)) {
      return null;
    }
    return (
      <ActionsDropdown
        link={linkToQuestions}
        buttonClassName='study-guide-actions-dropdown__btn unbutton'
        className='study-guide-actions-dropdown'
        guideLevel={guideLevel}
        showManageRestrictions={isAssessment}
        guideLevelTree={guideLevelTree}
      />
    );
  }

  openUpgradeSubjectModal = () => {
    this.setState({shouldShowUpgradeSubjectModal: true});
  };

  closeUpgradeSubjectModal = () => {
    this.setState({shouldShowUpgradeSubjectModal: false});
  };

  getStudyGuideLink = (linkToQuestions, queryString) => {
    const currentRoute =
      appStore.routerProps.location.pathname + appStore.routerProps.location.search;
    return {
      pathname: linkToQuestions,
      search: queryString,
      state: {
        from: currentRoute
      }
    };
  };

  onKeyPress = (e, onClick) => {
    if (e.key === 'Enter') {
      onClick(e);
    }
  };

  handleScroll = () => {
    const el = document.querySelector('.study-guide__sticky-search--mobile');
    if (el) {
      // handles logic for adding the box shadow to the bottom of the stickied nav
      const stickyTop = parseInt(window.getComputedStyle(el).top, 10);
      const currentTop = el.getBoundingClientRect().top;
      const stickyClass = 'study-guide__sticky-search--is-sticky';
      if (currentTop === stickyTop) {
        el.classList.add(stickyClass);
      } else {
        el.classList.remove(stickyClass);
      }
    }
  };

  render() {
    const {subject, licensedSubjects, themes, themeRefs, initialTheme, hasPopover} = this.state;
    const {
      guide,
      showAdvancedSearch,
      onSearchCallback,
      guideLevelsBySearchParams,
      isReady,
      partialCreditEnabled
    } = this.props;
    if (!subject || !guide) {
      return null;
    }
    const allGuideLevels = guide.getSortedGuideLevels();
    const guideLevels = allGuideLevels
      .filter((guideLevel) => guideLevel.hasQuestions())
      .filter(this.shouldDisplayGuideLevel);
    // Tree built from the full, unfiltered guide-level list so the indicators + modal can derive
    // effective-lock state (a parent lock cascades down; all children locked rolls up).
    const guideLevelTree = buildGuideTree(
      allGuideLevels
        .toArray()
        .map((level) => ({id: level.getId(), parentId: level.getParentId() || null}))
    );
    // Teacher indicator: roll child locks up to a parent (a quiz whose question types are all locked
    // shows the indicator). Built from the question-bearing tree so it's stable across search/display.
    const teacherRestrictedGuideLevelIds = getTeacherRestrictedGuideLevelIds(
      allGuideLevels.filter((guideLevel) => guideLevel.hasQuestions()).toArray()
    );
    // DQA #17841: flag a parent theme that contains any locked guide level (any-descendant roll-up).
    const guideLevelIdsWithLockedDescendant = getGuideLevelIdsWithLockedDescendant(
      allGuideLevels.filter((guideLevel) => guideLevel.hasQuestions()).toArray()
    );
    const hasTeacherAccess = sessionStore.isTeacher();
    const hasAccessToCreateTemplate = canCreateTemplateForSubject(subject.getId());
    return (
      <WindowSizeConsumer>
        {({breakpoints, viewportWidth}) => {
          const isMobileView = viewportWidth <= breakpoints.s;
          const hideThemeDropdownCTAText = viewportWidth <= breakpoints.xs;
          const isEmptySearches =
            isSearchMode() && guideLevelsBySearchParams.getCollection().isEmpty();
          return (
            <div className='study-guide-wrapper'>
              <div
                className={classnames('study-guide__sticky-search', {
                  // when popover backdrop is open, we need to increase the z-index of the sticky search by 1
                  'study-guide__sticky-search--mobile': isMobileView,
                  'study-guide__sticky-search--zIndex':
                    hasPopover && viewportWidth <= breakpoints.xs
                })}
              >
                <AdvancedSearch
                  showFilters={showAdvancedSearch}
                  key={appStore.routerProps.location.pathname}
                  onSearchCallback={onSearchCallback}
                  subjectId={subject.getId()}
                  isMobileView={isMobileView}
                  isEmptySearches={isEmptySearches}
                  themes={themes}
                  themeRefs={themeRefs}
                  hideThemeDropdownCTAText={hideThemeDropdownCTAText}
                  setHasPopover={(val) => this.setState({hasPopover: val})}
                />
              </div>
              {!isReady ? (
                <LoadingIndicator />
              ) : (
                <div className='study-guide-content'>
                  <div className='study-guide-content__split-pane'>
                    <ThemeMenu
                      themes={themes}
                      themeRefs={themeRefs}
                      initialTheme={initialTheme}
                      hideSidebar={isMobileView}
                    />
                    {isEmptySearches ? (
                      <NoResultsFound />
                    ) : (
                      <div className='study-guide-cards'>
                        <StudyGuideItemList>
                          {guideLevels.map((guideLevel, i, guideLevels) => {
                            let metadata;
                            if (isSearchMode()) {
                              const matchingQuestionsCount =
                                this.getMatchingQuestionCount(guideLevel);
                              metadata = (
                                <span className='study-guide__search-result'>
                                  {`${matchingQuestionsCount} ${pluralize(
                                    'question',
                                    matchingQuestionsCount
                                  )}`}
                                </span>
                              );
                            } else {
                              metadata =
                                guideLevel.getQuestionMetadataForSubjectView(hasTeacherAccess);
                            }

                            let queryString = '';
                            if (isSearchMode()) {
                              const searchQuery = {
                                ...this.props.location.query,
                                page: 1
                              };
                              queryString += qs.stringify(searchQuery, {addQueryPrefix: true});
                            }
                            const linkToQuestions = guideLevel.getLinkToQuestions();
                            const nextGuideLevel = guideLevels.get(i + 1);
                            const hasSubtopics =
                              nextGuideLevel &&
                              nextGuideLevel.getNlevel() === guideLevel.getNlevel() + 1;
                            const isLastOfTheme =
                              nextGuideLevel && nextGuideLevel.getNlevel() === 1;
                            const actionsByUserType = hasTeacherAccess ? (
                              this.generateActionsDropdown(
                                guideLevel,
                                hasAccessToCreateTemplate.value,
                                linkToQuestions + queryString,
                                guide.getGuideType() === constants.GUIDE_TYPES.ASSESSMENT,
                                guideLevelTree
                              )
                            ) : (
                              <AccuracyBadge
                                guideLevel={guideLevel}
                                metadata={metadata}
                                partialCreditEnabled={partialCreditEnabled}
                              />
                            );
                            /**
                             * We add **1** here to account for the `<HeroUnit>` containing a `h1`.
                             */
                            const headingLevel = guideLevel.getNlevel() + 1;
                            const isAssessment =
                              guide.getGuideType() === constants.GUIDE_TYPES.ASSESSMENT;
                            const isStudent = sessionStore.isStudent();
                            const retailStudentWithAccess =
                              isStudent &&
                              licensedSubjects.isEmpty() &&
                              subject.doesUserHaveAccess();
                            const isStudentWithoutAccess = isStudent && !retailStudentWithAccess;
                            const {isReady, value: isUnlicensedNonStudent} =
                              isUnlicensedTeacherOrAdmin();

                            let guideLevelActions = guideLevel.isTheme() ? null : actionsByUserType;

                            if (
                              guideLevel.isTheme() &&
                              guideLevel.isFree() &&
                              (!sessionStore.hasValidSession() || isStudentWithoutAccess)
                            ) {
                              guideLevelActions = <Icon icon='lock-open' />;
                            }
                            /*
                             * when a user can't access the guide level, we need to run a function instead of link
                             * right now, this is for an assessment guide, with unlicensed, logged out, or student users
                             */
                            const teacherOrAdminWithoutAccess =
                              isUnlicensedNonStudent && !subject.doesUserHaveAccess();
                            let isButton;
                            let buttonOnClick;
                            if (!sessionStore.hasValidSession() && isAssessment) {
                              isButton = true;
                              buttonOnClick = showGlobalLoginModal;
                            } else if (
                              isReady &&
                              isAssessment &&
                              (isStudentWithoutAccess || teacherOrAdminWithoutAccess)
                            ) {
                              isButton = true;
                              buttonOnClick = this.openUpgradeSubjectModal;
                            } else if (
                              isReady &&
                              !licensedSubjects.isEmpty() &&
                              !subject.doesUserHaveAccess() &&
                              sessionStore.hasValidSession()
                            ) {
                              isButton = true;
                              buttonOnClick = this.openUpgradeSubjectModal;
                            }

                            const guideLevelDisabled =
                              this.props.usePublicView &&
                              !guideLevel.isFree() &&
                              (guideLevel.isTheme() || hasSubtopics);

                            let typeProps = guideLevelDisabled
                              ? {as: 'li'}
                              : {
                                  as: Link,
                                  to: this.getStudyGuideLink(linkToQuestions, queryString)
                                };
                            if (this.props.usePublicView && !guideLevelDisabled && isSearchMode()) {
                              typeProps = {
                                as: 'li',
                                onClick: this.openUpgradeSubjectModal,
                                tabIndex: '0',
                                role: 'button',
                                onKeyPress: () => this.onKeyPress(this.openUpgradeSubjectModal)
                              };
                            } else if (isButton) {
                              typeProps = {
                                as: 'li',
                                onClick: buttonOnClick,
                                tabIndex: '0',
                                role: 'button',
                                onKeyPress: () => this.onKeyPress(buttonOnClick)
                              };
                            }

                            let classNames = 'decoration_none';
                            if (isLastOfTheme) {
                              classNames += ' sgi--last-of-theme';
                            }
                            if (guideLevelDisabled) {
                              classNames += ' disabled';
                            }

                            const actionClassNames = hasTeacherAccess ? 'teacher' : 'student';
                            const guideLevelRef = themeRefs[guideLevel.getId()] || null;

                            return (
                              <StudyGuideItem
                                aria-label={`${guideLevel.getName()}${
                                  guideLevel.isFree() ? '(Free)' : ''
                                }`}
                                className={classNames}
                                itemType={guideLevel.getGuideLevelType()}
                                key={guideLevel.id}
                                id={guideLevel.getUrlSlug()}
                                {...typeProps}
                              >
                                {guideLevel.isTheme() ? (
                                  <StudyGuideItem.Img src={guideLevel.getThemeImg()} />
                                ) : null}
                                <StudyGuideItem.Content>
                                  <StudyGuideItem.Heading as={`h${headingLevel}`}>
                                    <span className='gap-x1 flex items-center'>
                                      <MarkdownRendererV2 text={guideLevel.getName()} />
                                      {isAssessment &&
                                        teacherRestrictedGuideLevelIds.has(guideLevel.getId()) &&
                                        !guideLevel.isTheme() && (
                                          <span
                                            role='presentation'
                                            className='inline-flex'
                                            onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                            }}
                                          >
                                            <TeacherRestrictionIndicator placement='right-start' />
                                          </span>
                                        )}
                                      {/* DQA #17841: themes don't carry a lock themselves, so flag a
                                          theme that contains locked guide levels. */}
                                      {isAssessment &&
                                        guideLevel.isTheme() &&
                                        guideLevelIdsWithLockedDescendant.has(
                                          guideLevel.getId()
                                        ) && (
                                          <span
                                            role='presentation'
                                            className='inline-flex'
                                            onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                            }}
                                          >
                                            <WithTooltip content='Contains locked content'>
                                              <Icon
                                                icon='lock'
                                                iconStyle='solid'
                                                color='secondary'
                                                size='inherit'
                                              />
                                            </WithTooltip>
                                          </span>
                                        )}
                                    </span>
                                  </StudyGuideItem.Heading>
                                  <StudyGuideItem.Body>
                                    {!!guideLevel.getDescription() && (
                                      <MarkdownRendererV2 text={guideLevel.getDescription()} />
                                    )}
                                    {/* DQA #17841: the "Restricted at N schools" badge is a larger
                                      element, so it sits below the description instead of inline
                                      with the title. */}
                                    {isAssessment &&
                                      sessionStore.isSchoolAdministrator() &&
                                      !guideLevel.isTheme() && (
                                        <div
                                          role='presentation'
                                          onClick={(e) => {
                                            // Badge sits inside the guide-level row Link: preventDefault
                                            // stops the native-anchor reload; stopPropagation stops the
                                            // row's own click handler.
                                            e.preventDefault();
                                            e.stopPropagation();
                                          }}
                                        >
                                          <RestrictionIndicator
                                            guideLevelName={guideLevel.getName()}
                                            guideLevelId={guideLevel.getId()}
                                            guideLevelTree={guideLevelTree}
                                            showAsBadge
                                            usePortal
                                          />
                                        </div>
                                      )}
                                  </StudyGuideItem.Body>
                                  {!guideLevel.isTheme() && hasTeacherAccess ? (
                                    <StudyGuideItem.Meta>{metadata}</StudyGuideItem.Meta>
                                  ) : null}
                                </StudyGuideItem.Content>
                                <StudyGuideItem.Actions className={actionClassNames}>
                                  {guideLevelDisabled ? <Icon icon='lock' /> : guideLevelActions}
                                </StudyGuideItem.Actions>
                                <span ref={guideLevelRef} />
                              </StudyGuideItem>
                            );
                          })}
                        </StudyGuideItemList>
                      </div>
                    )}
                  </div>
                </div>
              )}
              {this.state.shouldShowUpgradeSubjectModal && (
                <UpgradeSubjectModal
                  subject={this.props.subject}
                  handleClose={this.closeUpgradeSubjectModal}
                />
              )}
            </div>
          );
        }}
      </WindowSizeConsumer>
    );
  }
}

const BootstrappedGuideLevelsList = withRouter(
  withApplicationMetadata(
    (props) => {
      const {subject, location} = props;
      const params = getSearchParams();

      // Caching tags, standards, and subject access queries since we know they are going to be used by children components
      // This allows us to fetch everything in a single load time
      const guideType = location.pathname.includes('assessments') ? 'assessment' : 'practice';

      const queries = [
        studyGuideUtils.getGuideBySubjectIdQuery(subject.getId(), guideType),
        userLicensedSubjectsQuery(),
        hasTagsQuery(subject.getId()),
        hasStandardsQuery(subject.getId()),
        getUserSubjectAccessQuery(subject.getId())
      ];

      if (sessionStore.hasValidSession()) {
        queries.push(
          studyGuideUtils.getClassroomsByUserIdQuery(
            sessionStore.getUserId(),
            sessionStore.isStudent()
          )
        );
      }

      if (isSearchMode()) {
        queries.push(
          getGuideLevelsBySearchParams({
            ...params,
            subjectId: subject.getId(),
            guideType
          })
        );
      }

      const {isReady, resources} = useMandarkQueries(queries);

      const [
        guide,
        licensedSubjects,
        __cachedTags,
        __cachedStandards,
        __cachedSubjectAccess,
        ...rest
      ] = resources;

      let classrooms = List();
      let guideLevelsBySearchParams = List();

      if (sessionStore.hasValidSession() && isSearchMode()) {
        [classrooms, guideLevelsBySearchParams] = rest;
      } else if (sessionStore.hasValidSession()) {
        [classrooms] = rest;
      } else if (isSearchMode()) {
        [guideLevelsBySearchParams] = rest;
      }

      const partialCreditEnabled = studyGuideUtils.isPartialCreditEnabledForSubject(
        subject,
        classrooms,
        sessionStore.isStudent()
      );

      return (
        <GuideLevelsList
          {...props}
          guide={guide}
          guideLevelsBySearchParams={guideLevelsBySearchParams}
          isReady={isReady}
          licensedSubjects={licensedSubjects}
          partialCreditEnabled={partialCreditEnabled}
        />
      );
    },
    (props) => {
      const guideType = props.location.pathname.includes('assessments') ? 'Assessment' : 'Practice';

      return {
        title: `${props.subject.getName()} | ${guideType} | Albert`,
        metaDescription: props.subject.getDescription()
      };
    }
  )
);

const StudyGuide = ({subject, user, onSearchCallback, location}) => {
  const {isReady, value: isUnlicensedNonStudent} = isUnlicensedTeacherOrAdmin();

  if (!isReady) {
    return <LoadingSpinner />;
  }

  const guideType = location.pathname.includes('assessments') ? 'assessment' : 'practice';
  const isAssessment = guideType === 'assessment';
  const usePublicView = showPublicViewForSubject(subject);
  const showAdvancedSearch = !(
    isAssessment &&
    (!sessionStore.hasValidSession() || isUnlicensedNonStudent || sessionStore.isStudent())
  );
  if (!showAdvancedSearch) {
    const params = getQueryParamsAsObject();
    if (!isEmpty(params)) {
      removeQueryParams(...Object.keys(params));
    }
  }
  return (
    <>
      <MarketingBanner user={user} subject={subject} className='px-x2' />
      <BootstrappedGuideLevelsList
        subject={subject}
        user={user}
        usePublicView={usePublicView}
        showAdvancedSearch={showAdvancedSearch}
        onSearchCallback={onSearchCallback}
      />
    </>
  );
};

StudyGuide.propTypes = {
  subject: PropTypes.object,
  user: PropTypes.instanceOf(UserModelV2),
  onSearchCallback: PropTypes.func,
  location: PropTypes.object
};

export default StudyGuide;
